import { isAgentRunRef } from "@/types/agent-summary"
import { isEntityRef } from "@/types/entity"
import { isTripRef } from "@/types/trip"

import type { DetailSheetHistoryEntry } from "@/lib/storage/detail-sheet-slice"
import { singleParam } from "@/lib/utils/url"

export const detailSheetHistoryDepthKey = "__mtDetailSheetHistoryDepth"

export type DetailSheetUrlLocator = { ref: string }

export type DetailSheetRefKind =
  | "entity"
  | "web_event"
  | "agent_run_report"
  | "action_pill_answer"
  | "trip"

const WebEventRefRE = /^we-[a-zA-Z0-9]{8}$/
const ActionPillRefRE = /^pi-[a-zA-Z0-9]{8}$/

export function isWebEventRef(value: string): value is `we-${string}` {
  return WebEventRefRE.test(value)
}

export function isActionPillRef(value: string): boolean {
  return ActionPillRefRE.test(value)
}

export function isDetailSheetRef(value: string): boolean {
  return (
    isEntityRef(value) ||
    isWebEventRef(value) ||
    isAgentRunRef(value) ||
    isActionPillRef(value) ||
    isTripRef(value)
  )
}

export function sheetKindForRef(ref: string): DetailSheetRefKind | undefined {
  if (isActionPillRef(ref)) return "action_pill_answer"
  if (isAgentRunRef(ref)) return "agent_run_report"
  if (isWebEventRef(ref)) return "web_event"
  if (isTripRef(ref)) return "trip"
  if (isEntityRef(ref)) return "entity"
  return undefined
}

function recordOrEmpty(value: unknown): Record<string, unknown> {
  if (typeof value === "object" && value !== null) {
    return { ...value }
  }

  return {}
}

export function getDetailSheetHistoryDepth(historyState: unknown): number {
  const state = recordOrEmpty(historyState)
  const depth = state[detailSheetHistoryDepthKey]

  if (typeof depth !== "number" || !Number.isInteger(depth) || depth < 0) {
    return 0
  }

  return depth
}

export function buildHistoryStateWithDetailSheetDepth(
  historyState: unknown,
  url: string,
  depth: number
): Record<string, unknown> {
  return {
    ...recordOrEmpty(historyState),
    [detailSheetHistoryDepthKey]: depth,
    as: url,
    url,
  }
}

export function locatorForEntry(
  entry: DetailSheetHistoryEntry
): DetailSheetUrlLocator | null {
  if (entry.kind === "entity" && entry.entityRef) {
    return { ref: entry.entityRef }
  }
  if (entry.kind === "agent_run_report" && entry.runRef) {
    return { ref: entry.runRef }
  }
  if (entry.kind === "action_pill_answer" && entry.pillRef) {
    return { ref: entry.pillRef }
  }
  if (entry.kind === "trip") {
    return { ref: entry.tripRef }
  }
  if (
    entry.kind === "search_result" &&
    entry.searchResult.ref &&
    isWebEventRef(entry.searchResult.ref)
  ) {
    return { ref: entry.searchResult.ref }
  }
  return null
}

export function locatorFromQuery(
  query: URLSearchParams
): DetailSheetUrlLocator | undefined {
  const ref = singleParam(query.get("ref"))
  if (ref && isDetailSheetRef(ref)) {
    return { ref }
  }

  return undefined
}

export function applyLocatorToQuery(
  query: URLSearchParams,
  locator: DetailSheetUrlLocator | undefined
): void {
  query.delete("ref")
  query.delete("run")

  if (!locator) return

  query.set("ref", locator.ref)
}

export function locatorsEqual(
  a: DetailSheetUrlLocator | undefined,
  b: DetailSheetUrlLocator | undefined
): boolean {
  if (!a && !b) return true
  if (!a || !b) return false
  return a.ref === b.ref
}

export function entryMatchesLocator(
  entry: DetailSheetHistoryEntry,
  locator: DetailSheetUrlLocator
): boolean {
  const entryLocator = locatorForEntry(entry)
  return entryLocator !== null && locatorsEqual(entryLocator, locator)
}

/**
 * Decides whether a query-only URL update driven by the detail sheet should use
 * history.replaceState vs history.pushState.
 */
export function detailSheetQueryHistoryOp(
  currentQuery: URLSearchParams,
  nextQuery: URLSearchParams
): "push" | "replace" | null {
  const prevLocator = locatorFromQuery(currentQuery)
  const nextLocator = locatorFromQuery(nextQuery)
  const prevQuote = singleParam(currentQuery.get("quoteRef")) ?? ""
  const nextQuote = singleParam(nextQuery.get("quoteRef")) ?? ""

  const locatorChanged = !locatorsEqual(prevLocator, nextLocator)
  const quoteChanged = prevQuote !== nextQuote

  if (!locatorChanged && !quoteChanged) {
    return null
  }

  if (locatorChanged) {
    if (!prevLocator && nextLocator) {
      return "push"
    }
    if (prevLocator && !nextLocator) {
      return "replace"
    }
    return "push"
  }

  return "replace"
}

export function getPopstateDetailSheetHistoryIndex(
  detailSheetOpen: boolean,
  detailSheetHistory: {
    stack: DetailSheetHistoryEntry[]
    currentIndex: number
  },
  targetLocator: DetailSheetUrlLocator | undefined
): number | null {
  if (!detailSheetOpen) {
    return null
  }

  if (targetLocator) {
    const historyIndex = detailSheetHistory.stack.findIndex((entry) =>
      entryMatchesLocator(entry, targetLocator)
    )

    if (
      historyIndex === -1 ||
      historyIndex === detailSheetHistory.currentIndex
    ) {
      return null
    }

    return historyIndex
  }

  for (let i = detailSheetHistory.currentIndex - 1; i >= 0; i--) {
    const entry = detailSheetHistory.stack[i]
    if (locatorForEntry(entry) === null) {
      return i
    }
  }

  return null
}
